import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Users, 
  TrendingUp, 
  Repeat, 
  Trophy,
  Star,
  Mail,
  Phone,
  Wallet,
  Target,
  Award,
  Crown
} from "lucide-react";
import { formatINR } from "@/lib/currency";

interface Participant {
  id: string;
  name: string;
  email: string;
  mobile: string;
  has_paid: boolean;
}

interface SplitPaymentRequest {
  id: string;
  product_name: string;
  total_price: number;
  split_amount: number;
  status: string;
  customer_name: string;
  customer_email: string;
  participants: Participant[];
}

interface CustomerInsightsProps {
  requests: SplitPaymentRequest[];
}

interface CustomerData {
  email: string;
  name: string;
  mobile?: string;
  totalContributions: number;
  totalAmount: number;
  participationCount: number;
  paidCount: number;
  conversionRate: number;
  lastActivity: string;
  isInitiator: boolean;
  initiatedCount: number;
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function CustomerInsights({ requests }: CustomerInsightsProps) {
  const insights = useMemo(() => {
    const customerMap = new Map<string, CustomerData>();
    const initiatorMap = new Map<string, { count: number; name: string }>();

    // Track initiators (customers who created split requests)
    requests.forEach((request) => {
      const email = request.customer_email.toLowerCase();
      const existing = initiatorMap.get(email) || { count: 0, name: request.customer_name };
      initiatorMap.set(email, { count: existing.count + 1, name: request.customer_name });
    });

    // Track all participants
    requests.forEach((request) => {
      request.participants.forEach((participant) => {
        const email = participant.email.toLowerCase();
        const existing = customerMap.get(email);
        
        if (existing) {
          existing.participationCount += 1;
          if (participant.has_paid) {
            existing.paidCount += 1;
            existing.totalAmount += Number(request.split_amount);
          }
          existing.totalContributions += 1;
        } else {
          const initiatorData = initiatorMap.get(email);
          customerMap.set(email, {
            email,
            name: participant.name,
            mobile: participant.mobile,
            totalContributions: 1,
            totalAmount: participant.has_paid ? Number(request.split_amount) : 0,
            participationCount: 1,
            paidCount: participant.has_paid ? 1 : 0,
            conversionRate: 0,
            lastActivity: new Date().toISOString(),
            isInitiator: !!initiatorData,
            initiatedCount: initiatorData?.count || 0,
          });
        }
      });
    });

    // Calculate conversion rates
    customerMap.forEach((customer) => {
      customer.conversionRate = customer.participationCount > 0 
        ? (customer.paidCount / customer.participationCount) * 100 
        : 0;
    });

    const customers = Array.from(customerMap.values());

    // Top contributors by amount
    const topContributors = [...customers]
      .sort((a, b) => b.totalAmount - a.totalAmount)
      .slice(0, 5);

    // Repeat customers (participated more than once)
    const repeatCustomers = customers
      .filter(c => c.participationCount > 1)
      .sort((a, b) => b.participationCount - a.participationCount);

    // High converters (100% payment rate with multiple participations)
    const loyalCustomers = customers
      .filter(c => c.conversionRate === 100 && c.participationCount >= 1)
      .sort((a, b) => b.totalAmount - a.totalAmount)
      .slice(0, 5);

    // Initiators who also pay
    const activeInitiators = customers
      .filter(c => c.isInitiator && c.paidCount > 0)
      .sort((a, b) => b.initiatedCount - a.initiatedCount);

    // Overall stats
    const totalUniqueCustomers = customers.length;
    const totalRepeatCustomers = repeatCustomers.length;
    const avgContribution = customers.length > 0 
      ? customers.reduce((sum, c) => sum + c.totalAmount, 0) / customers.filter(c => c.paidCount > 0).length 
      : 0;
    const overallConversionRate = customers.length > 0
      ? (customers.reduce((sum, c) => sum + c.paidCount, 0) / customers.reduce((sum, c) => sum + c.participationCount, 0)) * 100
      : 0;

    return {
      topContributors,
      repeatCustomers,
      loyalCustomers,
      activeInitiators,
      totalUniqueCustomers,
      totalRepeatCustomers,
      avgContribution: isNaN(avgContribution) ? 0 : avgContribution,
      overallConversionRate: isNaN(overallConversionRate) ? 0 : overallConversionRate,
    };
  }, [requests]);

  const getRankBadge = (index: number) => {
    if (index === 0) return <Crown className="h-4 w-4 text-yellow-500" />;
    if (index === 1) return <Trophy className="h-4 w-4 text-gray-400" />;
    if (index === 2) return <Award className="h-4 w-4 text-amber-600" />;
    return <span className="text-xs text-muted-foreground font-medium">#{index + 1}</span>;
  };

  if (requests.length === 0) {
    return (
      <div className="text-center py-12">
        <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium text-muted-foreground">No customer data yet</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Customer insights will appear once you have split payment orders.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Users className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Unique Customers</p>
                <p className="text-2xl font-bold">{insights.totalUniqueCustomers}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-lg">
                <Repeat className="h-4 w-4 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Repeat Customers</p>
                <p className="text-2xl font-bold">{insights.totalRepeatCustomers}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <Wallet className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Avg. Contribution</p>
                <p className="text-xl font-bold">{formatINR(insights.avgContribution)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Target className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Payment Rate</p>
                <p className="text-2xl font-bold">{insights.overallConversionRate.toFixed(0)}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Top Contributors */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="h-4 w-4 text-yellow-500" />
              Top Contributors
            </CardTitle>
            <CardDescription>Customers with highest total contributions</CardDescription>
          </CardHeader>
          <CardContent>
            {insights.topContributors.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No paid contributions yet
              </p>
            ) : (
              <div className="space-y-3">
                {insights.topContributors.map((customer, index) => (
                  <div 
                    key={customer.email} 
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-6">
                        {getRankBadge(index)}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{customer.name}</p>
                        <p className="text-xs text-muted-foreground">{customer.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-green-600">{formatINR(customer.totalAmount)}</p>
                      <p className="text-xs text-muted-foreground">
                        {customer.paidCount} payment{customer.paidCount !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Loyal Customers (100% payment rate) */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Star className="h-4 w-4 text-yellow-500" />
              Loyal Customers
            </CardTitle>
            <CardDescription>Customers with 100% payment success rate</CardDescription>
          </CardHeader>
          <CardContent>
            {insights.loyalCustomers.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No loyal customers yet
              </p>
            ) : (
              <div className="space-y-3">
                {insights.loyalCustomers.map((customer, index) => (
                  <div 
                    key={customer.email} 
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-6">
                        <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{customer.name}</p>
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-muted-foreground">{customer.participationCount} splits</p>
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-green-500/10 text-green-600 border-green-500/20">
                            100% paid
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <p className="font-semibold text-green-600">{formatINR(customer.totalAmount)}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Repeat Customers */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Repeat className="h-4 w-4 text-purple-500" />
              Repeat Customers
            </CardTitle>
            <CardDescription>Customers participating in multiple splits</CardDescription>
          </CardHeader>
          <CardContent>
            {insights.repeatCustomers.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No repeat customers yet
              </p>
            ) : (
              <div className="space-y-3">
                {insights.repeatCustomers.slice(0, 5).map((customer) => (
                  <div 
                    key={customer.email} 
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{customer.name}</p>
                        {customer.isInitiator && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                            Initiator
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{customer.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{customer.participationCount} splits</p>
                      <div className="flex items-center gap-1 justify-end">
                        <Progress 
                          value={customer.conversionRate} 
                          className="w-16 h-1.5" 
                        />
                        <span className="text-xs text-muted-foreground">
                          {customer.conversionRate.toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Customer Engagement Overview */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-blue-500" />
              Engagement Overview
            </CardTitle>
            <CardDescription>Overall customer engagement metrics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Repeat Rate</span>
                <div className="flex items-center gap-2">
                  <Progress 
                    value={insights.totalUniqueCustomers > 0 ? (insights.totalRepeatCustomers / insights.totalUniqueCustomers) * 100 : 0} 
                    className="w-24 h-2" 
                  />
                  <span className="text-sm font-medium">
                    {insights.totalUniqueCustomers > 0 
                      ? ((insights.totalRepeatCustomers / insights.totalUniqueCustomers) * 100).toFixed(0) 
                      : 0}%
                  </span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Payment Success</span>
                <div className="flex items-center gap-2">
                  <Progress 
                    value={insights.overallConversionRate} 
                    className="w-24 h-2" 
                  />
                  <span className="text-sm font-medium">{insights.overallConversionRate.toFixed(0)}%</span>
                </div>
              </div>

              <div className="pt-3 border-t">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-primary">{insights.topContributors.length}</p>
                    <p className="text-xs text-muted-foreground">Active Payers</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-purple-600">{insights.loyalCustomers.length}</p>
                    <p className="text-xs text-muted-foreground">100% Reliable</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
